import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FormContext,
  FormContextProvider,
  SchemaFieldsWithContextUI,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { QuestionFeature, QuestionType } from '@stormwater-software/lib-inspection-templates';
import React, { MouseEventHandler, useContext } from 'react';
import { useQuery } from 'react-query';
import QuestionGroupContext from '../../../../contexts/QuestionGroupContext';
import TemplateEditorContext from '../../../../contexts/TemplateEditorContext';
import { questionApi } from '../../../../utils/api/question';
import { questionConfigApi } from '../../../../utils/api/questionConfig';
import { questionConfigValueApi } from '../../../../utils/api/questionConfigValue';
import { questionGroupApi } from '../../../../utils/api/questionGroup';
import { questionTypeApi } from '../../../../utils/api/questionType';
import { FormModal } from '../../../Shared/form';
import Loading from '../../../Shared/ResourceIndex/Loading';

/** Wider grids tend to distort the generated PDF, so we advise staying at/below this. */
const RECOMMENDED_MAX_COLUMNS = 8;

export interface IGridQuestionDetailFormProps {
  title?: string;
  onCancel: MouseEventHandler;
  onSubmit: () => void;
  isUsedTemplate?: boolean;
}

/**
 * Grid question editor.
 *
 * On submit it builds a single composite question type from the selected column
 * types (e.g. Text + Number => controlTypes ["text", "number"], isSystem false),
 * stores the column names on the group's `table_headers`, and creates one
 * `inspection_question` per row pointing at that composite type.
 *
 * - isUsedTemplate === false: fully editable. Reducing rows deletes the extra
 *   questions; editing columns re-points the remaining rows at the new type.
 * - isUsedTemplate === true: columns are locked and rows may only be increased;
 *   the extra rows are appended to inspection_questions.
 */
const GridQuestionDetailForm: React.FC<IGridQuestionDetailFormProps> = ({
  onCancel,
  onSubmit,
  isUsedTemplate = false,
}) => {
  const templateEditorContext = useContext(TemplateEditorContext);
  const questionGroupContext = useContext(QuestionGroupContext);

  const questionTypes = templateEditorContext.questionTypes || [];
  const optionsTypeIds = templateEditorContext.questionsWithOptions || [];
  const group = questionGroupContext.questionGroup;

  const existingQuestions = (group.questions || [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const existingCount = existingQuestions.length;

  const firstQuestionId =
    existingCount > 0 ? existingQuestions[0].id : undefined;
  const configValuesQuery = useQuery(
    ['questionConfigValues', firstQuestionId],
    () => questionConfigValueApi.index(firstQuestionId as number),
    { enabled: !!firstQuestionId },
  );

  /** Base (single control-type) types offered in the per-column picker. */
  const baseTypeForControl = (control: string): QuestionType | undefined =>
    questionTypes.find(
      (qt) =>
        qt.isSystem &&
        qt.controlTypes.length === 1 &&
        qt.controlTypes[0] === control,
    );

  /** The stored (shared) option list for this grid, if any. */
  const getStoredOptions = (): string[] => {
    const optionsItem = (configValuesQuery.data || []).find(
      (v: any) => v.questionTypeFieldId != null && v.value != null,
    );

    if (!optionsItem) {
      return [];
    }

    try {
      return JSON.parse((optionsItem as any).value);
    } catch {
      return [];
    }
  };

  const getSchema = (formData: Record<string, any>) => {
    const columns = Number(formData.number_of_columns) || 0;
    const rows = Number(formData.number_of_rows) || 0;
    let selectedColumnKey: string | null = null;
    const isOneOptionsQuestionTypeSelected = Object.keys(formData).some(
      (key) => {
        const result =
          key.startsWith('column_') &&
          key.endsWith('_type') &&
          optionsTypeIds.includes(Number(formData[key]));

        if (result) {
          selectedColumnKey = key;
        }

        return result;
      },
    );

    const columnTypeOptions = (columnKey: string) =>
      questionTypes.filter((qt) => {
        if (
          isOneOptionsQuestionTypeSelected &&
          optionsTypeIds.includes(qt.id) &&
          selectedColumnKey !== columnKey
        ) {
          return false;
        }

        return (
          qt.isSystem &&
          qt.controlTypes[0] !== 'descriptive_text' &&
          (qt.controlTypes[0] as string) !== 'latitudeLongitude'
        );
      });

    const schema: any = {
      number_of_columns: {
        controlType: UIControlType.number,
        label: 'Number of Columns',
        validation: { required: true, min: 2 },
        disabled: isUsedTemplate,
      },
      number_of_rows: {
        controlType: UIControlType.number,
        label: 'Number of Rows',
        validation: { required: true, min: isUsedTemplate ? existingCount : 1 },
      },
    };

    for (let i = 1; i <= columns; i++) {
      schema[`column_${i}_name`] = {
        controlType: UIControlType.text,
        label: `Column ${i} Name`,
        validation: { required: true },
        style: { flex: '1 1 50%' },
        disabled: isUsedTemplate,
      };
      schema[`column_${i}_type`] = {
        controlType: UIControlType.select,
        label: `Column ${i} Type`,
        options: columnTypeOptions(`column_${i}_type`),
        labelKey: 'label',
        valueKey: 'id',
        validation: { required: true },
        style: { flex: '1 1 50%' },
        disabled: isUsedTemplate,
      };

      if (
        isOneOptionsQuestionTypeSelected &&
        selectedColumnKey === `column_${i}_type`
      ) {
        schema[`column_${i}_options`] = {
          label: 'Options',
          controlType: UIControlType.optionList,
          validation: { required: true },
        };
      }
    }

    if (formData.has_label) {
      schema['row_header'] = {
        controlType: UIControlType.text,
        label: 'Row Header',
        validation: { required: false },
      };
      for (let j = 0; j < rows; j++) {
        schema[`row_${j + 1}_label`] = {
          controlType: UIControlType.text,
          label: `Row ${j + 1} Label`,
          validation: { required: false },
        };
      }
    }

    schema['has_label'] = {
      controlType: UIControlType.checkbox,
      label: 'Show Label',
      defaultValue: true,
    };

    schema['carry_over'] = {
      controlType: UIControlType.checkbox,
      label: 'Carry Over',
      defaultValue: false,
    };

    return schema;
  };

  const getInitialValues = () => {
    if (existingCount > 0) {
      const compositeType = questionTypes.find(
        (qt) => qt.id === existingQuestions[0].question_type_id,
      );
      const controlTypes = compositeType ? compositeType.controlTypes : [];
      const headers = group.table_headers || [];
      const storedOptions = getStoredOptions();

      // A leading row-label column is stored as an extra first entry in
      // `table_headers`, so its presence is detected by the header count.
      const hasLabel = headers.length > controlTypes.length;
      const columnHeaders = hasLabel ? headers.slice(1) : headers;

      const values: Record<string, any> = {
        number_of_columns: controlTypes.length || 2,
        number_of_rows: existingCount,
        has_label: hasLabel,
        row_header: hasLabel ? headers[0] || '' : '',
        carry_over: (existingQuestions[0].features || []).includes(
          QuestionFeature.carryOver,
        ),
      };

      controlTypes.forEach((control, idx) => {
        const baseType = baseTypeForControl(control);

        values[`column_${idx + 1}_name`] = columnHeaders[idx] || '';
        values[`column_${idx + 1}_type`] = baseType ? baseType.id : undefined;

        if (baseType && optionsTypeIds.includes(baseType.id)) {
          values[`column_${idx + 1}_options`] = storedOptions;
        }
      });

      if (hasLabel) {
        existingQuestions.forEach((q, idx) => {
          values[`row_${idx + 1}_label`] = q.input_label || '';
        });
      }

      return values;
    }

    return {
      number_of_columns: 2,
      number_of_rows: 1,
      has_label: false,
      carry_over: false,
    };
  };

  const handleSubmit = async (data: Record<string, any>) => {
    const numColumns = Number(data.number_of_columns) || 0;
    const numRows = Number(data.number_of_rows) || 0;
    const hasLabel = !!data.has_label;
    const carryOver = !!data.carry_over;

    const columnTypes: QuestionType[] = [];
    const columnNames: string[] = [];
    let optionsColumnIndex = 0;

    for (let i = 1; i <= numColumns; i++) {
      const typeId = Number(data[`column_${i}_type`]);
      const baseType = questionTypes.find((qt) => qt.id === typeId);

      if (baseType) {
        columnTypes.push(baseType);
      }
      columnNames.push(data[`column_${i}_name`]);

      if (optionsTypeIds.includes(typeId)) {
        optionsColumnIndex = i;
      }
    }

    // 1. Build (find-or-create) the composite question type.
    const controlTypes = columnTypes.reduce<string[]>(
      (acc, t) => acc.concat(t.controlTypes),
      [],
    );
    const label = columnTypes.map((t) => t.label).join(' + ');
    const compositeType = await questionTypeApi.create({
      label,
      controlTypes,
      isSystem: false,
    });

    // 2. Persist headers on the group
    const headers = hasLabel
      ? [data.row_header || '', ...columnNames]
      : columnNames;

    await questionGroupApi.update(templateEditorContext.templateId, group.id!, {
      table_headers: headers,
    });

    // 3. Reconcile rows, collecting every final row's id.
    const rowIds: number[] = [];

    if (numRows >= existingCount) {
      existingQuestions.forEach((q) => rowIds.push(q.id!));

      for (let i = existingCount; i < numRows; i++) {
        const newQuestion = await questionApi.create(group.id!, {
          groupId: group.id!,
          question_type_id: compositeType.id,
          sort_order: i,
          features: [],
          input_label: '',
        } as any);

        rowIds.push(newQuestion.id!);
      }
    } else if (!isUsedTemplate) {
      const toDelete = existingQuestions.slice(numRows);

      await Promise.all(toDelete.map((q) => questionApi.delete(q.id!)));
      existingQuestions.slice(0, numRows).forEach((q) => rowIds.push(q.id!));
    } else {
      existingQuestions.forEach((q) => rowIds.push(q.id!));
    }

    // 4. When columns changed on an editable template, re-point kept rows.
    if (!isUsedTemplate) {
      await Promise.all(
        existingQuestions
          .slice(0, numRows)
          .filter((q) => q.question_type_id !== compositeType.id)
          .map((q) =>
            questionApi.update(q.id!, {
              question_type_id: compositeType.id,
            } as any),
          ),
      );
    }

    // 4b. Persist each row's label (input_label) and the carry-over feature.
    if (hasLabel || carryOver) {
      await Promise.all(
        rowIds.map((questionId, idx) =>
          questionApi.update(questionId, {
            input_label: hasLabel ? data[`row_${idx + 1}_label`] || '' : '',
            features: carryOver ? [QuestionFeature.carryOver] : [],
          } as any),
        ),
      );
    }

    // 5. Persist the shared option list on every row's config value.
    if (optionsColumnIndex > 0) {
      const optionsValue = data[`column_${optionsColumnIndex}_options`] || [];
      const configFields = await questionConfigApi.index();
      const optionsField = configFields.find(
        (f: any) =>
          f.questionTypeId === compositeType.id && f.fieldName === 'options',
      );

      if (optionsField) {
        await Promise.all(
          rowIds.map((questionId) =>
            questionConfigApi.update([
              {
                questionId,
                questionFieldId: optionsField.id,
                value: JSON.stringify(optionsValue),
              },
            ]),
          ),
        );
      }
    }

    onSubmit();
  };

  if (
    templateEditorContext.questionTypes === null ||
    (!!firstQuestionId && configValuesQuery.isLoading)
  ) {
    return <Loading />;
  }

  return (
    <FormContextProvider>
      <FormContext.Consumer>
        {(formContext) => (
          <FormModal
            onCancel={onCancel}
            onSubmit={handleSubmit}
            modalProps={{
              title:
                existingQuestions.length > 0
                  ? 'Edit Grid Question'
                  : 'Add Grid Question',
            }}
            submitDisabled={
              formContext.value.number_of_columns < 2 ||
              formContext.value.number_of_rows < 1
            }
          >
            {Number(formContext.value.number_of_columns) >
              RECOMMENDED_MAX_COLUMNS && (
              <p className='grid-column-warning'>
                <FontAwesomeIcon icon='exclamation-triangle' />
                For the best results we recommend keeping columns at{' '}
                {RECOMMENDED_MAX_COLUMNS} or fewer. Wider grids may appear
                distorted in the generated PDF.
              </p>
            )}

            <SchemaFieldsWithContextUI
              schema={getSchema(formContext.value)}
              initialFormData={getInitialValues()}
            />
          </FormModal>
        )}
      </FormContext.Consumer>
    </FormContextProvider>
  );
};

export default GridQuestionDetailForm;
